import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Input, Button, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { getUser, REAUTH_TIMEOUT_MS } from '../variables/constants';
import { reauthBus } from '../utils/reauthBus';
import badgeCarteBrune from '../assets/images/logo.png';

/**
 * Revalidation de la session : un ECRAN, et non une fenetre par-dessus l'ecran.
 *
 * POURQUOI PLUS DE COMPTE A REBOURS. L'ancienne version affichait les minutes qui
 * tombaient, en 28 px, juste au-dessus du champ de mot de passe. Une horloge qui
 * decompte pendant qu'on cherche son mot de passe ne protege rien de plus - elle
 * met l'agent sous pression au seul moment ou il doit se concentrer, et la
 * frappe fautive qu'elle provoque coute un essai de plus.
 *
 * LE DELAI EXISTE TOUJOURS, il ne se montre simplement plus : une phrase le dit
 * une fois, au calme. La premiere touche frappee le REPOUSSE D'UNE FENETRE, UNE
 * SEULE FOIS - quelqu'un qui repond est present, et personne ne doit se faire
 * ejecter en pleine saisie faute d'avoir vu l'heure tourner.
 *
 * UNE SEULE FOIS, ET C'EST LE POINT IMPORTANT. Annuler le delai pour de bon, ce
 * que faisait une premiere version de cet ecran, suffisait a le desarmer d'une
 * touche : un ecran ouvert, effleure puis abandonne, ne se serait plus jamais
 * ferme et la session serait restee vivante indefiniment. L'echeance reste donc
 * absolue, elle est seulement portee a deux fenetres au pire.
 *
 * CE QUE L'ECRAN DIT EN PREMIER, c'est que le travail en cours est sauf. C'est
 * vrai au sens strict : les requetes refusees attendent dans la file de
 * `reauthBus` et sont rejouees des que le mot de passe est accepte. La phrase
 * n'est donc pas un lot de consolation, c'est l'information la plus utile de
 * l'ecran - et celle que l'ancienne fenetre, tout occupee a decompter, ne
 * donnait pas.
 *
 * UN ECRAN PLUTOT QU'UNE MODALE, enfin, parce que c'est ce qui se passe
 * reellement : l'application n'est plus utilisable tant qu'on n'a pas repondu.
 * Une boite flottante au-dessus d'une page encore visible suggere le contraire
 * et invite a cliquer derriere.
 */

const VERT = '#1a6b3c';
const INK = '#111827';
const INK_MUTED = '#6b7280';

export default function ReauthGate() {
  const { t } = useTranslation('commun');
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form] = Form.useForm();
  const minuterie = useRef(null);
  // Le report n'est accorde qu'une fois par ouverture : voir l'en-tete du fichier.
  const reportAccorde = useRef(false);

  // L'utilisateur peut changer d'une session a l'autre : on le fige a l'ouverture.
  const user = useMemo(() => (open ? getUser() : null), [open]);
  const email = user?.email ?? user?.login ?? '';

  const annulerMinuterie = useCallback(() => {
    if (minuterie.current) {
      clearTimeout(minuterie.current);
      minuterie.current = null;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    annulerMinuterie();
    try { await authService.logout(); } catch (_) { /* la session part quand meme */ }
    reauthBus.rejectAllAndLogout();
    window.location.href = '/login';
  }, [annulerMinuterie]);

  useEffect(() => {
    return reauthBus.subscribe((evt) => {
      if (evt.type === 'open') {
        setOpen(true);
        setError(null);
        form.resetFields();
      } else if (evt.type === 'close') {
        setOpen(false);
      }
    });
  }, [form]);

  /* Le delai de securite, invisible. Il s'arme a l'ouverture et tombe avec elle. */
  const armerMinuterie = useCallback(() => {
    annulerMinuterie();
    minuterie.current = setTimeout(handleLogout, REAUTH_TIMEOUT_MS);
  }, [annulerMinuterie, handleLogout]);

  useEffect(() => {
    if (!open) { annulerMinuterie(); return undefined; }
    reportAccorde.current = false;
    armerMinuterie();
    return annulerMinuterie;
  }, [open, armerMinuterie, annulerMinuterie]);

  /** Repondre vaut report, mais une seule fois : l'echeance reste bornee. */
  const reporterUneFois = useCallback(() => {
    if (reportAccorde.current) return;
    reportAccorde.current = true;
    armerMinuterie();
  }, [armerMinuterie]);

  /* L'ecran couvre tout : le defilement du document dessous n'a plus lieu d'etre,
     et le laisser actif ferait glisser un fond qu'on ne peut de toute facon pas
     atteindre. */
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const avant = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = avant; };
  }, [open]);

  const handleSubmit = async ({ password }) => {
    setSubmitting(true);
    setError(null);
    try {
      await authService.refreshTokenWithPassword(email, password);
      reauthBus.resolveAll(); // ferme l'ecran via l'evenement 'close'
    } catch (e) {
      const msg = e?.response?.data?.message
                ?? e?.response?.data?.error
                ?? t('reauthGate.motDePasseIncorrect');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reauth-titre"
      style={{
        /* L'ecran s'arrete SOUS la banniere d'environnement, il ne la recouvre
           pas. Elle dit sur quelle base on travaille - test ou production -, et
           c'est au moment de ressaisir son mot de passe qu'on a le plus besoin de
           le savoir. Elle est posee en `fixed` a z-index 2000 et publie sa hauteur
           dans `--cb-banniere-h` (0 quand il n'y en a pas) : on s'y adosse, et on
           reste un cran en dessous d'elle. */
        position: 'fixed',
        top: 'var(--cb-banniere-h, 0px)', left: 0, right: 0, bottom: 0,
        zIndex: 1999,
        background: '#f7f8fa',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, overflowY: 'auto',
      }}
    >
      {/* LE CADRE EST CARRE, SON CONTENU NE L'EST PAS. L'angle droit du cadre le
          pose comme un ecran, non comme une boite de dialogue flottante ; les
          champs et le bouton gardent l'arrondi qu'ils ont partout ailleurs dans
          l'application. La forme du contenant dit ce qu'est cet ecran, celle des
          commandes dit comment on s'en sert. */}
      <div
        style={{
          width: '100%', maxWidth: 480,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 0,
          padding: '36px 32px',
          boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 20px 40px -28px rgba(16,24,40,0.35)',
        }}
      >
        {/* Le badge de la Carte Brune porte l'autorite - on ne redemande pas son mot
            de passe au nom d'un logiciel, mais au nom du bureau -, le nom de
            l'application dit ou l'on se trouve. Les deux cote a cote, comme sur
            l'ecran de connexion : c'est le meme geste, au meme nom. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img
            src={badgeCarteBrune}
            alt={t('reauthGate.logoAlt')}
            style={{ height: 56, width: 'auto', display: 'block', flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 19, fontWeight: 700, color: VERT, lineHeight: 1.2 }}>
              CBConnect
            </div>
            <div style={{ fontSize: 12.5, color: INK_MUTED, lineHeight: 1.3 }}>
              BNCB Togo
            </div>
          </div>
        </div>

        <h1
          id="reauth-titre"
          style={{ margin: '20px 0 8px', fontSize: 20, fontWeight: 600, color: INK }}
        >
          {t('reauthGate.titre')}
        </h1>

        <p style={{ margin: '0 0 24px', fontSize: 13.5, color: INK_MUTED, lineHeight: 1.6 }}>
          {t('reauthGate.rassurance')}
        </p>

        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
          <Form.Item label={t('reauthGate.email')}>
            <Input value={email} disabled style={{ borderRadius: 10, height: 44 }} />
          </Form.Item>

          <Form.Item
            name="password"
            label={t('reauthGate.motDePasse')}
            rules={[{ required: true, message: t('reauthGate.motDePasseRequis') }]}
          >
            {/* Repondre, c'est etre la : la premiere touche repousse l'echeance - une
                fois, pas indefiniment. */}
            <Input.Password
              autoFocus
              onChange={reporterUneFois}
              style={{ borderRadius: 10, height: 44 }}
            />
          </Form.Item>

          {error && (
            <Alert type="error" message={error} showIcon style={{ marginBottom: 16, borderRadius: 10 }} />
          )}

          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            block
            style={{ height: 46, borderRadius: 10, fontWeight: 600, background: VERT, borderColor: VERT }}
          >
            {t('reauthGate.continuer')}
          </Button>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap', marginTop: 18,
          }}>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              {t('reauthGate.fermetureSansReponse')}
            </span>
            <Button type="link" onClick={handleLogout} style={{ padding: 0, height: 'auto' }}>
              {t('reauthGate.seDeconnecter')}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
